import { Injectable } from '@angular/core';
import { Constants } from 'src/app/models/constants';

@Injectable({
  providedIn: 'root'
})
export class SemanticVersioningService {

  constructor() {
  }

  /**
   * Returns the pattern of our versioning, to be used for validation purposes
   *
   * @returns the versioning pattern
   */
  getVersioningPattern(): string {
    return Constants.VERSION_PATTERN;
  }

  /**
   * Returns the initial version to be used
   *
   * @returns the initial version
   */
  getInitialVersion(): string {
    return Constants.VERSION_DEFAULT_INITIAL_SEMANTIC_VERSION;
  }

  /**
   * Prepares the next version based on the default configuration, by incrementing the desired level
   *
   * @param previousVersion - the previous version that's going to serve for creating a newer one
   * @param level - the semantic version level to increment
   * @returns the newest version
   */
  getNextVersion(previousVersion: string, level: string): string {
    let version = this.getVersionAsArray(previousVersion);
    const levelToIncrement = this.determineLevelToIncrement(level);
    version = this.increment(version, levelToIncrement);
    return this.getVersionAsString(version);
  }

  /**
   * Increments the required level of the version to get a newer one
   *
   * @param level - the level to be incremented
   * @returns the newer version
   */
  increment(version: Array<number>, level: string) {
    const idx = this.getRespectiveLevelIndex(level);
    version[idx] += Constants.VERSION_DEFAULT_INCREMENTATION_STEP;
    return version;
  }

  /**
   * Checks if the level that user gave as input is there
   * If so, we increment that level, otherwise we use the default one
   *
   * @param level - the level to be incremented
   * @returns the correct level based on the check
   */
  determineLevelToIncrement(level: string) {
    return level !== null ? level : Constants.VERSION_DEFAULT_LEVEL_TO_INCREMENT;
  }

  /**
   * Prepares the index for each semantic version level
   *
   * @param level - the semantic version level
   * @returns - the respective index
   */
  getRespectiveLevelIndex(level: string): number {
    switch(level) {
      case Constants.VERSION_LEVEL_MAJOR: return 0;
      case Constants.VERSION_LEVEL_MINOR: return 1;
      case Constants.VERSION_LEVEL_PATCH: return 2;
    }
  }

  /**
   * Compares versions in their semantic format
   *
   * @param v1 - the first version
   * @param v2 - the second version
   * @returns true if the first version is less than the second, false otherwise
   */
  compareVersions(v1, v2): boolean {
    const [majorV1, minorV1, patchV1] = v1.split('.').map(v => Number.parseInt(v));
    const [majorV2, minorV2, patchV2] = v2.split('.').map(v => Number.parseInt(v));

    if (!isNaN(majorV2) && majorV1 !== majorV2) {
      return majorV1 > majorV2 ? false : true;
    }

    if(!isNaN(minorV2) && minorV1 !== minorV2) {
      return minorV1 > minorV2 ? false : true;
    }

    if(!isNaN(patchV2) && patchV1 > patchV2) {
      return false;
    }

    return true;
  }

  /**
   * Prepares an Array represantation of version
   *
   * @param version - the version as a String
   * @returns the version as an Array of numbers
   */
    getVersionAsArray(version: string): Array<number> {
    return version.split('.').map(v => +v);
  }

  /**
   * Prepares the String represantation of version
   *
   * @param version - the version as Array
   * @returns the version as String
   */
  getVersionAsString(version: Array<number>): string {
    return version.join('.');
  }
}
